import React from 'react';
import type { Itinerary } from '../../types';

type LineageEntry = NonNullable<Itinerary['lineage']>[number];

interface LineageLineProps {
  lineage: LineageEntry[];
  navigate: (to: string) => void;
}

/**
 * The provenance line under a plan's title.
 *
 * Named entries render as they always have: the creator's name, clickable to
 * that plan. A `private` entry is the server's placeholder for an ancestor
 * this reader may not be named (the server ends the chain there): it renders
 * as plain text — no name, no link, no "another traveler" — because it
 * carries no plan id and no name to show.
 */
export function LineageLine({ lineage, navigate }: LineageLineProps) {
  if (lineage.length === 0) return null;

  if (lineage[0].private) {
    return <p className="iv2-info-forked">Adapted from a private plan</p>;
  }

  return (
    <p className="iv2-info-forked">
      Based on a plan by{' '}
      {lineage.map((l, i) => {
        if (l.private) {
          return (
            <React.Fragment key={`private-${i}`}>
              {', who adapted it from '}
              <span>a private plan</span>
            </React.Fragment>
          );
        }
        const planId = l.plan_id;
        return (
          <React.Fragment key={planId ?? `entry-${i}`}>
            {i > 0 && ', who got it from '}
            <span
              className="iv2-info-lineage-name"
              onClick={(e) => {
                e.stopPropagation();
                if (planId != null) navigate(`/itinerary/${planId}`);
              }}
            >
              {l.name || 'another traveler'}
            </span>
          </React.Fragment>
        );
      })}
    </p>
  );
}

export default LineageLine;
